import React, { useEffect, useState } from 'react';
import { formatDeDay, formatDeRange, formatDeRangeFull, presetRange, todayIso, validateRange } from '../utils/dateRange';

// The "Benutzerdefiniert" row + its unfolding Von/Bis drawer, rendered as the
// last entry of the Zeitraum popover on both the Tasks and the Dashboard page.
// Only the type scale differs between the two hosts (`compact`), so this lives
// in one place rather than being duplicated into both popovers.
//
// The drawer renders INSIDE the popover, which is what keeps the pages' existing
// mousedown outside-click listeners working untouched: clicks land inside the
// popover ref, and the browser's native date picker is chrome that never
// dispatches a page-level mousedown at all.

const REDUCED_MOTION = typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Collapses every transition to 'none' under prefers-reduced-motion, matching
// how App.css already handles the setting.
const motion = (value) => (REDUCED_MOTION ? 'none' : value);

// Date-only reduction of the Dashboard "Empfang" widget's control group
// (DashboardPage ABS_GROUP_STYLE): one bordered box, borderless input inside.
// Empfang pins a hard width; here the drawer's width is the authority instead,
// because Chrome and Firefox give input[type=date] different intrinsic widths.
const GROUP_STYLE = {
  display: 'flex', alignItems: 'center', flex: 1, minWidth: 0,
  border: '1px solid var(--border-color)', borderRadius: 6,
  background: '#fff', padding: '0 8px', height: 34,
  transition: motion('border-color 0.15s ease, box-shadow 0.15s ease'),
};

const INPUT_STYLE = {
  border: 'none', outline: 'none', background: 'transparent',
  fontFamily: 'inherit', color: 'var(--text-primary)',
  padding: 0, cursor: 'pointer', width: '100%', minWidth: 0,
};

const CHIP_STYLE = {
  padding: '3px 10px', borderRadius: 999,
  border: '1px solid var(--border-color)', background: '#fff',
  fontSize: '0.78rem', color: 'var(--text-secondary)',
  fontFamily: 'inherit', cursor: 'pointer',
  transition: motion('border-color 0.15s ease, color 0.15s ease'),
};

const QUICK_PRESETS = [
  { key: 'lastMonth', label: 'Letzter Monat' },
  { key: 'lastQuarter', label: 'Letztes Quartal' },
];

const DateRangeDrawer = ({ open, committed, seed, onToggle, onApply, compact = false }) => {
  const [draft, setDraft] = useState({ from: '', to: '' });
  const [focused, setFocused] = useState(null);

  // A committed range IS the selected state — the hosts pass `committed` only
  // while their filter sits on 'range', so no separate `active` prop is needed.
  const active = !!(committed?.from && committed?.to);

  // Re-seed on every open: from the committed range if there is one, else from
  // the preset that's currently active — so the drawer opens on an editable
  // range rather than two empty fields. Discards any abandoned draft, which is
  // what makes Escape / outside-click "cancel" without extra bookkeeping.
  useEffect(() => {
    if (!open) return;
    const base = (committed?.from && committed?.to) ? committed : seed;
    setDraft({ from: base?.from || '', to: base?.to || '' });
    setFocused(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const check = validateRange(draft.from, draft.to);
  const today = todayIso();
  const labelSize = compact ? '0.72rem' : '0.75rem';
  const inputSize = compact ? '0.9rem' : '0.95rem';
  const committedLabel = (committed?.from && committed?.to)
    ? formatDeRange(committed.from, committed.to)
    : '';

  const apply = () => { if (check.valid) onApply({ ...draft }); };

  const groupStyle = (field) => ({
    ...GROUP_STYLE,
    // Only the offending field turns coral; 'to' is the one the user must fix.
    ...(check.level === 'error' && field === 'to' ? { borderColor: 'var(--color-tertiary)' } : {}),
    ...(focused === field
      ? { borderColor: 'var(--color-secondary)', boxShadow: '0 0 0 3px rgba(47,107,215,0.15)' }
      : {}),
  });

  const field = (key, label) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontSize: labelSize, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.04em', color: 'var(--text-secondary)', marginBottom: 5,
      }}>
        {label}
      </div>
      {/* The visible field is a read-only text input in German format; the native
          date input sits on top at opacity 0 to supply the picker. Same overlay
          as CalendarPage — a native date input renders in the BROWSER's locale,
          so an en-US browser would otherwise show 07/30/2026 here. */}
      <div style={{ ...groupStyle(key), position: 'relative' }}>
        {/* Purely visual mirror of the real input below — hidden from the a11y
            tree so a screen reader doesn't announce an unlabeled duplicate. */}
        <input
          type="text"
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          value={formatDeDay(draft[key])}
          placeholder="TT.MM.JJJJ"
          style={{ ...INPUT_STYLE, fontSize: inputSize }}
        />
        <span aria-hidden="true" style={{
          flexShrink: 0, fontSize: inputSize, lineHeight: 1,
          opacity: 0.55, pointerEvents: 'none',
        }}>
          📅
        </span>
        <input
          type="date"
          lang="de"
          aria-label={label}
          value={draft[key]}
          max={today}
          onChange={(e) => setDraft(prev => ({ ...prev, [key]: e.target.value }))}
          onFocus={() => setFocused(key)}
          onBlur={() => setFocused(null)}
          style={{
            position: 'absolute', left: 0, top: 0, width: '100%', height: '100%',
            opacity: 0, border: 'none', padding: 0, margin: 0, cursor: 'pointer',
          }}
        />
      </div>
    </div>
  );

  return (
    <>
      {/* Trigger row — same geometry as the preset rows above it, plus a chevron */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); }
        }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          padding: '5px 16px', cursor: 'pointer', fontSize: '0.95rem',
          borderTop: '1px solid #eee', marginTop: 4, paddingTop: 9,
          transition: motion('background 0.15s ease'),
          background: active ? '#f0f4ff' : 'transparent',
          fontWeight: active ? 600 : 400,
          color: active ? 'var(--color-secondary)' : 'inherit',
        }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f8f9fa'; }}
        onMouseLeave={e => { e.currentTarget.style.background = active ? '#f0f4ff' : 'transparent'; }}
      >
        <span>
          Benutzerdefiniert
          {committedLabel && (
            <span
              title={formatDeRangeFull(committed.from, committed.to)}
              style={{
                display: 'block', fontSize: '0.8rem', fontWeight: 400,
                color: 'var(--text-secondary)', marginTop: 1,
              }}
            >
              {committedLabel}
            </span>
          )}
        </span>
        <svg
          width="12" height="12" viewBox="0 0 12 12" style={{
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: motion('transform 200ms cubic-bezier(0.16,1,0.3,1)'),
          }}
        >
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {/* Drawer — height is deterministic, so a maxHeight transition is enough.
          Expand eases out slowly with the opacity trailing behind; collapse is
          quicker with the opacity leading, so nothing is seen clipping. */}
      <div style={{
        overflow: 'hidden',
        maxHeight: open ? 210 : 0,
        opacity: open ? 1 : 0,
        transform: open ? 'translateY(0)' : 'translateY(-4px)',
        transition: motion(open
          ? 'max-height 220ms cubic-bezier(0.16,1,0.3,1), transform 220ms cubic-bezier(0.16,1,0.3,1), opacity 150ms ease 70ms'
          : 'max-height 160ms cubic-bezier(0.4,0,0.2,1), transform 160ms cubic-bezier(0.4,0,0.2,1), opacity 90ms ease'),
      }}>
        <div
          // Enter anywhere in the drawer applies — except on the chips and the
          // Übernehmen button, where preventDefault would cancel the button's own
          // activation and "Letzter Monat" would apply the draft instead of
          // setting itself.
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target.tagName !== 'BUTTON') { e.preventDefault(); apply(); }
          }}
          style={{
            background: 'var(--table-header-bg)',
            borderTop: '1px solid var(--border-light)',
            padding: '14px 16px',
            borderRadius: '0 0 5px 5px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 0 }}>
            {field('from', 'Von')}
            <div style={{
              width: 20, textAlign: 'center', lineHeight: '34px',
              color: 'var(--text-secondary)', fontSize: inputSize,
            }}>
              –
            </div>
            {field('to', 'Bis')}
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
            {QUICK_PRESETS.map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => setDraft(presetRange(p.key))}
                style={CHIP_STYLE}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'var(--color-secondary)';
                  e.currentTarget.style.color = 'var(--color-secondary)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                  e.currentTarget.style.color = 'var(--text-secondary)';
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 10, marginTop: 12,
          }}>
            <span style={{
              fontSize: '0.78rem',
              fontVariantNumeric: 'tabular-nums',
              color: check.level === 'error' || check.level === 'warn'
                ? 'var(--color-tertiary)'
                : 'var(--text-secondary)',
            }}>
              {check.message}
            </span>
            <button
              type="button"
              onClick={apply}
              disabled={!check.valid}
              style={{
                flexShrink: 0,
                padding: '6px 14px', borderRadius: 6, border: 'none',
                background: 'var(--color-secondary)', color: '#fff',
                fontSize: '0.85rem', fontWeight: 600, fontFamily: 'inherit',
                cursor: check.valid ? 'pointer' : 'default',
                opacity: check.valid ? 1 : 0.45,
                transition: motion('filter 0.15s ease, opacity 0.15s ease'),
              }}
              onMouseEnter={e => { if (check.valid) e.currentTarget.style.filter = 'brightness(1.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
            >
              Übernehmen
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DateRangeDrawer;
